import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/shared/service/auth.service';
import { EcklerRouteConfig } from 'src/app/shared/eckler-route-config';
import { UserTypeEnum } from 'src/app/shared/enum/user-type-enum';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';

@Component({
  selector: 'app-auth-callback',
  template: `
    <div class="loading-container">
      <mat-spinner></mat-spinner>
      <p>{{ 'PROCESSING_AUTHENTICATION' | translate }}</p>
    </div>
  `,
  styles: [`
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      gap: 20px;
    }
    
    p {
      font-size: 16px;
      color: #666;
    }
  `]
})
export class AuthCallbackComponent implements OnInit {

  constructor(
    private authService: AuthService,
    private auth0: Auth0Service,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Get the authorization code from the URL parameters
    const code = this.route.snapshot.queryParamMap.get('code');
    const state = this.route.snapshot.queryParamMap.get('state');
    const error = this.route.snapshot.queryParamMap.get('error');

    console.log('Auth0 callback parameters:', { code, state, error });

    if (error) {
      console.error('Auth0 callback error:', error);
      this.redirectToLogin();
      return;
    }

    if (code) {
      // Handle the Auth0 callback - let Auth0 SDK handle the token exchange
      this.handleAuth0Callback();
    } else {
      // No code parameter, redirect to login
      this.redirectToLogin();
    }
  }

  private handleAuth0Callback(): void {
    // The Auth0 SDK will automatically handle the callback and set the user
    // We just need to wait for the authentication state to change
    this.auth0.isAuthenticated$.subscribe(isAuthenticated => {
      console.log('Auth0 isAuthenticated:', isAuthenticated);
      if (isAuthenticated) {
        // Get user info and access token, then call our backend
        this.auth0.user$.subscribe(user => {
          if (user) {
            this.auth0.getAccessTokenSilently().subscribe({
              next: (token) => {
                // Exchange Auth0 token with backend
                this.authService.exchangeAuth0Token(token, user).subscribe({
                  next: (authResult) => {
                    // Store the authentication result
                    localStorage.setItem('ecklerUser', JSON.stringify(authResult));
                    
                    // Set current user in auth service
                    this.authService.updateCurrentUser(authResult);
                    
                    // Backend authentication successful
                    this.redirectUser(authResult);
                  },
                  error: (error) => {
                    console.error('Backend authentication failed:', error);
                    console.log('Checking for requiresAuth0Logout flag:', error.error?.requiresAuth0Logout);
                    
                    // Check if the error requires Auth0 logout
                    if (error.error && error.error.requiresAuth0Logout) {
                      console.log('User not found in system, performing Auth0 logout...');
                      this.authService.signOut(); // This will call auth0.logout()
                    } else {
                      this.redirectToLogin();
                    }
                  }
                });
              },
              error: (error) => {
                console.error('Failed to get Auth0 access token:', error);
                this.redirectToLogin();
              }
            });
          }
        });
      } else {
        // Not authenticated, redirect to login
        this.redirectToLogin();
      }
    });
  }

  private redirectUser(authResult: any): void {
    // Create user from auth result and set in auth service
    const user = this.authService.createUserFromAuthenticateResult(authResult);
    
    if (user.userType === UserTypeEnum.SuperUser) {
      this.router.navigate([EcklerRouteConfig.ecklerPlanListRoute()]);
    } else if (user.userType === UserTypeEnum.Administrator) {
      this.router.navigate([EcklerRouteConfig.planPlanMembersListRoute()]);
    } else if (user.userType === UserTypeEnum.Member) {
      this.router.navigate([EcklerRouteConfig.planMemberPortalRoute()]);
    } else {
      // Fallback to login if user type is not recognized
      this.redirectToLogin();
    }
  }

  private redirectToLogin(): void {
    this.router.navigate([EcklerRouteConfig.loginRoute('')]);
  }
}